import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * #1621 (D439, D463): the Grounds list as a bottom sheet over a full-screen map, at phone width. A region of the page, not a
 * dialog: a plain <section> pinned to the bottom of the map area, so nothing else on the page is hidden or trapped. Three
 * snap heights (a share of the map area); the handle is its own 44px button (a tap, or Enter, cycles low, mid, full, low)
 * and can also be dragged: it snaps to the nearest point on release.
 */
export const SHEET_SNAPS = [0.28, 0.6, 1];
export const nextSheetSnap = (snap) => SHEET_SNAPS[(SHEET_SNAPS.indexOf(snap) + 1) % SHEET_SNAPS.length];
export const handleLabel = (snap) => (snap === SHEET_SNAPS[SHEET_SNAPS.length - 1] ? "Show the map" : "Show more grounds");
/** The snap point nearest to a height ratio (0 to 1 of the map area). */
export const nearestSnap = (ratio) => SHEET_SNAPS.reduce((best, s) => (Math.abs(s - ratio) < Math.abs(best - ratio) ? s : best), SHEET_SNAPS[0]);

const DRAG_SLOP = 6;
const reducedMotion = () => typeof window !== "undefined" && Boolean(window.matchMedia) && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function GroundsSheet({ children, onFull }) {
  const [snap, setSnap] = useState(SHEET_SNAPS[0]);
  // Tells the screen when the sheet covers the whole map, so the map's controls (hidden under it) leave the tab order.
  useEffect(() => {
    if (onFull) onFull(snap === SHEET_SNAPS[SHEET_SNAPS.length - 1]);
  }, [snap, onFull]);
  const [dragH, setDragH] = useState(null); // px while a finger is dragging, else null
  const drag = useRef(null);
  const sectionRef = useRef(null);

  const areaHeight = () => (sectionRef.current && sectionRef.current.parentElement ? sectionRef.current.parentElement.clientHeight : 0);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const area = areaHeight();
    if (!area) return;
    drag.current = { id: e.pointerId, y: e.clientY, startH: snap * area, area, moved: false };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      // a synthetic pointer without a capture target: the drag still tracks the events the handle receives
    }
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.abs(dy) < DRAG_SLOP) return;
    d.moved = true;
    setDragH(Math.max(SHEET_SNAPS[0] * d.area, Math.min(d.area, d.startH - dy)));
  };
  const onPointerEnd = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (!d.moved) return;
    const dy = e.clientY - d.y;
    // A drag that ends on the handle is not a tap: swallow the click that may follow it, and only that one.
    suppressClick.current = true;
    setTimeout(() => { suppressClick.current = false; }, 0);
    setSnap(nearestSnap(Math.max(0, Math.min(1, (d.startH - dy) / d.area))));
    setDragH(null);
  };
  const suppressClick = useRef(false);
  const onClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    setSnap(nextSheetSnap(snap));
  };

  const full = snap === SHEET_SNAPS[SHEET_SNAPS.length - 1] && dragH === null;
  return (
    <section
      ref={sectionRef}
      aria-label="Grounds list"
      data-testid="grounds-sheet"
      data-nw-grounds-sheet-content=""
      data-snap={snap}
      style={{
        position: "absolute",
        insetInline: 0,
        bottom: 0,
        height: dragH === null ? `${snap * 100}%` : `${dragH}px`,
        // At full the search and chips (they float above the sheet) keep their place: the sheet starts under them.
        paddingTop: full ? "calc(var(--nw-sheet-float-h, 0px) + 20px)" : 0,
        boxSizing: "border-box",
        transition: dragH !== null || reducedMotion() ? "none" : "height 200ms ease, padding-top 200ms ease",
        zIndex: 900,
        display: "flex",
        flexDirection: "column",
        background: "var(--nw-bg, var(--nw-surface))",
        borderTop: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card) var(--nw-radius-card) 0 0",
        boxShadow: "0 -2px 12px rgba(0,0,0,0.18)",
        color: "var(--nw-ink)",
      }}
    >
      <button
        type="button"
        data-testid="grounds-sheet-handle"
        aria-label={handleLabel(snap)}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        className="flex items-center justify-center"
        style={{ flex: "0 0 auto", width: "100%", height: 44, background: "none", border: "none", cursor: "grab", touchAction: "none", color: "var(--nw-ink-muted)" }}
      >
        <span aria-hidden="true" style={{ width: 40, height: 4, borderRadius: 2, background: "var(--nw-line)" }} />
      </button>
      <div data-testid="grounds-sheet-list" style={{ flex: "1 1 0", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
        {children}
      </div>
    </section>
  );
}

/**
 * The search, chips and result line that float over the map. It publishes its height as `--nw-sheet-float-h` on the
 * sheet's root, so the map's control pill (a Leaflet control, top-right) can start below it instead of under the search.
 */
export function SheetFloat({ children }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const root = el && el.parentElement;
    if (!root) return undefined;
    const publish = () => root.style.setProperty("--nw-sheet-float-h", `${el.offsetHeight}px`);
    publish();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} data-nw-sheet-float="">
      {children}
    </div>
  );
}
